import { useEffect, useRef, useState } from "react";
import { CheckCircle2, X } from "lucide-react";

/**
 * V153 — Real, privacy-safe social-proof ticker.
 *
 * Pulls anonymised captions from `/api/public/social-proof` (which reads
 * genuine `registrations` records and strips PII). Never fabricates data:
 * if the endpoint returns an empty list, the ticker hides itself entirely.
 *
 * Hidden on:
 *   - /admin/*
 *   - Sessions where the visitor already dismissed it
 *   - When the backend feed is empty or unreachable
 */
const SHOW_MS = 6000;
const GAP_MS = 14000;
const REFRESH_MS = 5 * 60 * 1000; // refetch every 5 min in case admin toggles

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function SocialProofTicker() {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(
        () => typeof window !== "undefined" && sessionStorage.getItem("at_proof_dismissed") === "1",
    );
    const mountedRef = useRef(true);

    // Fetch the anonymised feed
    useEffect(() => {
        if (dismissed) return;
        if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;

        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(`${BACKEND}/api/public/social-proof?limit=12`, {
                    credentials: "omit",
                    cache: "no-store",
                });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                const list = Array.isArray(data?.items) ? data.items : [];
                setItems(list);
            } catch (_e) {
                // Silent: feed unavailable → hide popup entirely
            }
        };
        load();
        const iv = setInterval(load, REFRESH_MS);
        return () => { cancelled = true; clearInterval(iv); };
    }, [dismissed]);

    // Rotate through items
    useEffect(() => {
        mountedRef.current = true;
        if (dismissed || items.length === 0) return undefined;
        if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return undefined;

        let showTimer, hideTimer;
        const cycle = (i) => {
            if (!mountedRef.current) return;
            setIdx(i);
            setVisible(true);
            hideTimer = setTimeout(() => {
                if (!mountedRef.current) return;
                setVisible(false);
                showTimer = setTimeout(() => cycle((i + 1) % items.length), GAP_MS);
            }, SHOW_MS);
        };
        // Initial delay so it doesn't fire on page load
        showTimer = setTimeout(() => cycle(0), 9000);
        return () => {
            mountedRef.current = false;
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [dismissed, items]);

    if (dismissed || !visible || items.length === 0) return null;
    const it = items[idx];
    if (!it || !it.text) return null;

    return (
        <div
            className="fixed bottom-20 left-3 sm:left-5 z-30 max-w-[300px] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 pr-9 animate-fade-up"
            data-testid="social-proof-ticker"
            role="status"
            aria-live="polite"
        >
            <button
                onClick={() => {
                    setDismissed(true);
                    try { sessionStorage.setItem("at_proof_dismissed", "1"); } catch (_e) { /* ignore */ }
                }}
                aria-label="Dismiss"
                data-testid="social-proof-dismiss"
                className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700"
            >
                <X className="w-3 h-3" />
            </button>
            <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                </div>
                <div className="text-[13px] leading-snug min-w-0">
                    <div className="text-slate-800" data-testid="social-proof-text">{it.text}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{it.when || "Recently"}</div>
                </div>
            </div>
        </div>
    );
}
